<template>
  <div class="verify-email-page min-h-screen flex pt-20">
    <div class="container mx-auto px-4 mt-8">
      <div class="flex justify-center">
        <div class="w-full max-w-md">
          <UiCard class="shadow-sm">
            <UiCardContent class="p-6">
              <!-- Loading State -->
              <div v-if="verifying" class="text-center">
                <div class="mb-4">
                  <svg class="animate-spin mx-auto h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
                <h2 class="text-xl font-semibold mb-2">Verifying your email...</h2>
                <p class="text-sm text-muted-foreground">Please wait while we verify your account.</p>
              </div>

              <!-- Success State -->
              <div v-else-if="verificationStatus === 'success'" class="text-center">
                <div class="mb-4">
                  <svg class="mx-auto h-12 w-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 48 48">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                </div>
                <h2 class="text-xl font-semibold mb-2 text-green-800">Email verified successfully!</h2>
                <p class="text-sm text-muted-foreground mb-6">
                  Your account has been verified. You can now sign in to access your account.
                </p>
                <UiButton 
                  @click="goToLogin" 
                  class="w-full bg-black hover:bg-black/90"
                >
                  Continue to Sign In
                </UiButton>
              </div>

              <!-- Error State -->
              <div v-else-if="verificationStatus === 'error'" class="text-center">
                <div class="mb-4">
                  <svg class="mx-auto h-12 w-12 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 48 48">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                </div>
                <h2 class="text-xl font-semibold mb-2 text-red-800">Verification failed</h2>
                <p class="text-sm text-muted-foreground mb-4">
                  {{ errorMessage || 'The verification link is invalid or has expired.' }}
                </p>
                
                <div class="space-y-3">
                  <UiButton 
                    @click="requestNewVerification" 
                    class="w-full bg-blue-600 hover:bg-blue-700"
                    :disabled="resendLoading"
                  >
                    <svg v-if="resendLoading" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {{ resendLoading ? 'Sending...' : 'Send New Verification Email' }}
                  </UiButton>
                  
                  <UiButton 
                    @click="goToLogin" 
                    variant="outline"
                    class="w-full"
                  >
                    Back to Sign In
                  </UiButton>
                </div>
              </div>
              
              <!-- Resend Success Message -->
              <div v-if="resendSuccess" class="mt-4 p-3 bg-green-50 text-green-700 border border-green-200 rounded-md text-sm">
                A new verification email has been sent. Please check your inbox and click the verification link.
              </div>
            </UiCardContent>
          </UiCard>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import UiCard from '@/components/ui/card.vue';
import UiCardContent from '@/components/ui/card-content.vue';
import UiButton from '@/components/ui/button.vue';
import apiService from '@/services/api.service';

export default {
  name: 'VerifyEmailView',
  components: {
    UiCard,
    UiCardContent,
    UiButton
  },
  data() {
    return {
      verifying: true,
      verificationStatus: null, // 'success', 'error', or null
      errorMessage: '',
      resendLoading: false,
      resendSuccess: false,
      userEmail: ''
    };
  },
  async mounted() {
    await this.verifyEmail();
  },
  methods: {
    async verifyEmail() {
      const token = this.$route.query.token;
      
      if (!token) {
        this.verifying = false;
        this.verificationStatus = 'error';
        this.errorMessage = 'No verification token provided.';
        return;
      }

      try {
        const response = await apiService.verifyEmail(token);
        
        // If we get here, the request was successful
        this.verificationStatus = 'success';
        // Store user email if returned for potential resend functionality
        if (response.data && response.data.email) {
          this.userEmail = response.data.email;
        }
      } catch (error) {
        console.error('Email verification error:', error);
        this.verificationStatus = 'error';
        
        // Extract error message from API response
        if (error.response && error.response.data && error.response.data.message) {
          this.errorMessage = error.response.data.message;
        } else {
          this.errorMessage = 'Verification failed. Please try again.';
        }
        
        // Store user email if returned for resend functionality
        if (error.response && error.response.data && error.response.data.email) {
          this.userEmail = error.response.data.email;
        }
      } finally {
        this.verifying = false;
      }
    },
    
    async requestNewVerification() {
      if (!this.userEmail) {
        // If we don't have the user's email, redirect to registration
        this.$router.push('/register');
        return;
      }

      this.resendLoading = true;
      this.resendSuccess = false;

      try {
        await apiService.resendVerification(this.userEmail);
        
        // If we get here, the request was successful
        this.resendSuccess = true;
        // Optionally reset the error state
        this.verificationStatus = null;
        this.errorMessage = '';
      } catch (error) {
        console.error('Resend verification error:', error);
        
        // Extract error message from API response
        if (error.response && error.response.data && error.response.data.message) {
          this.errorMessage = error.response.data.message;
        } else {
          this.errorMessage = 'Failed to send verification email. Please try again.';
        }
      } finally {
        this.resendLoading = false;
      }
    },
    
    goToLogin() {
      this.$router.push('/login');
    }
  }
};
</script>
